import type { StationId } from '@/domain/types';
import type { VariableValuesRepository, VariableValueSnapshot } from '@/persistence/ports';
import type { Evse3Database, VariableValueRow } from '../db';

/**
 * Session 19b — Dexie-backed OCPP 2.0.1 Device Model store. The composite
 * primary key `[stationId+component+evseId+connectorId+variable+
 * attributeType]` is the spec's full Variable identity; the repo strips
 * `stationId` on read so callers see the version-neutral snapshot.
 */
export class VariableValuesRepositoryDexie implements VariableValuesRepository {
  constructor(private readonly db: Evse3Database) {}

  async byStation(stationId: StationId): Promise<readonly VariableValueSnapshot[]> {
    const rows = await this.db.variableValues.where('stationId').equals(stationId).toArray();
    return rows.map(strip);
  }

  async upsert(stationId: StationId, snap: VariableValueSnapshot): Promise<void> {
    await this.db.variableValues.put({ stationId, ...snap });
  }

  async seedMissing(
    stationId: StationId,
    snaps: readonly VariableValueSnapshot[],
  ): Promise<void> {
    await this.db.transaction('rw', this.db.variableValues, async () => {
      for (const snap of snaps) {
        const key: [StationId, string, number, number, string, string] = [
          stationId,
          snap.component,
          snap.evseId,
          snap.connectorId,
          snap.variable,
          snap.attributeType,
        ];
        const existing = await this.db.variableValues.get(key);
        if (existing === undefined) {
          await this.db.variableValues.put({ stationId, ...snap });
        }
      }
    });
  }

  async clear(stationId: StationId): Promise<void> {
    await this.db.variableValues.where('stationId').equals(stationId).delete();
  }
}

function strip(row: VariableValueRow): VariableValueSnapshot {
  const { stationId: _stationId, ...rest } = row;
  return rest;
}
